/**
 * Approval Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  ApprovalEntity,
  ApprovalCountsEntity,
  ApprovalsResultEntity,
  PaginationInfoEntity,
  MonthEndChecklistAttachmentEntity,
} from "../../domain/entities/ApprovalEntity";
import {
  ApprovalDTO,
  ApprovalCountsResponseDTO,
  ApprovalsResponseDTO,
  PaginationDTO,
  MonthEndChecklistAttachmentDTO,
} from "../models/ApprovalDTOs";

export class ApprovalMapper {
  /**
   * Map MonthEndChecklistAttachmentDTO to MonthEndChecklistAttachmentEntity
   */
  static toAttachmentEntity(
    dto: MonthEndChecklistAttachmentDTO,
  ): MonthEndChecklistAttachmentEntity {
    return {
      id: dto.id,
      checklistItemId: dto.checklist_item_id,
      fileName: dto.file_name,
      originalFilename: dto.original_filename,
      fileSize: dto.file_size,
      mimeType: dto.mime_type,
      s3Bucket: dto.s3_bucket,
      s3Key: dto.s3_key,
      s3Url: dto.s3_url,
      uploadedBy: dto.uploaded_by,
      uploadedAt: dto.uploaded_at,
      uploadedByName: dto.uploaded_by_name,
      uploadedByEmail: dto.uploaded_by_email,
    };
  }

  /**
   * Map ApprovalDTO to ApprovalEntity
   */
  static toApprovalEntity(dto: ApprovalDTO): ApprovalEntity {
    return {
      id: dto.id,
      userId: dto.user_id,
      reviewerId: dto.reviewer_id,
      moduleCode: dto.module_code,
      moduleName: dto.module_name,
      actionCode: dto.action_code,
      actionName: dto.action_name,
      entityType: dto.entity_type,
      entityId: dto.entity_id,
      status: dto.status,
      requestComments: dto.request_comments,
      reviewComments: dto.review_comments,
      reviewedAt: dto.reviewed_at,
      isApplied: dto.is_applied,
      createdAt: dto.created_at,
      updatedAt: dto.updated_at,
      requesterName: dto.requester_name,
      requesterEmail: dto.requester_email,
      reviewerName: dto.reviewer_name,
      reviewerEmail: dto.reviewer_email,
      changeDetails: dto.change_details,
      // Month-end checklist specific fields
      itemTitle: dto.item_title,
      itemDescription: dto.item_description,
      accountingArea: dto.accounting_area,
      itemStatus: dto.item_status,
      itemNotes: dto.item_notes,
      attachments: dto.attachments?.map((att) => this.toAttachmentEntity(att)),
    };
  }

  /**
   * Map PaginationDTO to PaginationInfoEntity
   */
  static toPaginationInfoEntity(dto: PaginationDTO): PaginationInfoEntity {
    return {
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset,
      hasMore: dto.has_more,
    };
  }

  /**
   * Map ApprovalsResponseDTO to ApprovalsResultEntity
   */
  static toApprovalsResultEntity(
    dto: ApprovalsResponseDTO,
  ): ApprovalsResultEntity {
    return {
      approvals: dto.data.reviews.map((review) =>
        this.toApprovalEntity(review),
      ),
      pagination: this.toPaginationInfoEntity(dto.data.pagination),
    };
  }

  /**
   * Map ApprovalCountsResponseDTO to ApprovalCountsEntity
   * Backend may return journal_template separately; merge into journal for correct totals.
   */
  static toApprovalCountsEntity(
    dto: ApprovalCountsResponseDTO,
  ): ApprovalCountsEntity {
    const counts = dto.data;
    const empty = { pending: 0, approved: 0, rejected: 0 };
    const j = counts.journal || empty;
    const jt = counts.journal_template || empty;
    return {
      invoice: counts.invoice || empty,
      bill: counts.bill || empty,
      customer: counts.customer || empty,
      vendor: counts.vendor || empty,
      journal: {
        pending: (j.pending || 0) + (jt.pending || 0),
        approved: (j.approved || 0) + (jt.approved || 0),
        rejected: (j.rejected || 0) + (jt.rejected || 0),
      },
      ap_ar_journal: counts.ap_ar_journal || empty,
      bank_transaction: counts.bank_transaction || {
        pending: 0,
        approved: 0,
        rejected: 0,
      },
      bank_account: counts.bank_account || empty,
      credit_note: counts.credit_note || {
        pending: 0,
        approved: 0,
        rejected: 0,
      },
      debit_note: counts.debit_note || empty,
      month_end_checklist_item: counts.month_end_checklist_item || empty,
      gl: counts.gl || empty,
    };
  }
}
